<template>
  <GenericCard
    :locked="locked"
    :on-action="submitForm"
    :invalid="$v.peParty.$invalid"
    :dirty="$v.peParty.$anyDirty"
    title="Background Overview"
    :view-only="viewOnly"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col>
          <v-text-field
            v-model="peParty.num_entries"
            autocomplete="off"
            label="# Entries US"
            outlined
            type="number"
            :error-messages="getErrorMessages($v.peParty.num_entries)"
            @input="$v.peParty.num_entries.$touch()"
            @blur="$v.peParty.num_entries.$touch()"
          />
        </v-col>
        <v-col>
          <ComboDate
            class="mt-3 ml-1"
            label="First Entry US"
            :date="peParty.date_first_entry"
            :uncertain="peParty.date_first_entry_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.peParty.date_first_entry)"
            @update-box="onDateFirstEntryUncertainUpdate"
            @update-date="onDateFirstEntryUpdate"
            @tab-out="$v.peParty.date_first_entry.$touch()"
          />
        </v-col>
        <v-col>
          <ComboDate
            class="mt-3 ml-1"
            label="Last Entry US"
            :date="peParty.date_last_entry"
            :uncertain="peParty.date_last_entry_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.peParty.date_last_entry)"
            @update-box="onDateLastEntryUncertainUpdate"
            @update-date="onDateLastEntryUpdate"
            @tab-out="$v.peParty.date_last_entry.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="auto">
          <label for="departed_since_first_entry">Departed US since date of first entry?</label>
          <v-radio-group
            id="departed_since_first_entry"
            v-model="peParty.departed_since_first_entry"
            row
            :error-messages="getErrorMessages($v.peParty.departed_since_first_entry)"
            class="mt-0"
            @input="$v.peParty.departed_since_first_entry.$touch()"
            @blur="$v.peParty.departed_since_first_entry.$touch()"
          >
            <v-radio
              label="No"
              :value="false"
            />
            <v-radio
              label="Yes"
              :value="true"
            />
          </v-radio-group>
        </v-col>
        <v-col cols="auto">
          <label for="any_enforcement_history">Notable immigration enforcement history?</label>
          <v-radio-group
            id="any_enforcement_history"
            v-model="peParty.any_enforcement_history"
            row
            :error-messages="getErrorMessages($v.peParty.any_enforcement_history)"
            class="mt-0"
            @input="$v.peParty.any_enforcement_history.$touch()"
            @blur="$v.peParty.any_enforcement_history.$touch()"
          >
            <v-radio
              label="No"
              :value="false"
            />
            <v-radio
              label="Yes"
              :value="true"
            />
          </v-radio-group>
        </v-col>
        <v-col cols="auto">
          <label for="any_benefits_history">Prior immigration benefits history?</label>
          <v-radio-group
            id="any_benefits_history"
            v-model="peParty.any_benefits_history"
            row
            :error-messages="getErrorMessages($v.peParty.any_benefits_history)"
            class="mt-0"
            @input="$v.peParty.any_benefits_history.$touch()"
            @blur="$v.peParty.any_benefits_history.$touch()"
          >
            <v-radio
              label="No"
              :value="false"
            />
            <v-radio
              label="Yes"
              :value="true"
            />
          </v-radio-group>
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import ComboDate from '@/components/Fields/ComboDate.vue'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard.vue'

export default {
  name: 'BackgroundOverviewCard',
  components: { ComboDate, GenericCard },

  props: {
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  validations () {
    return {
      peParty: {
        any_benefits_history: {},
        any_enforcement_history: {},
        confirm_departed_since_first_entry: {},
        date_first_entry: {},
        date_first_entry_uncertain: {},
        date_last_entry: {},
        date_last_entry_uncertain: {},
        departed_since_first_entry: {},
        num_entries: {},
      },
    }
  },

  computed: {
    locked: {
      get () {
        return this.PeBackgroundOverviewCardLock
      },

      set (value) {
        this.setPeBackgroundOverviewCardLock(value)
      },
    },

    optionsFatherStatuses () { return this.getCachedDropdown('parties/father_status') },
    optionsMotherStatuses () { return this.getCachedDropdown('parties/mother_status') },

    ...mapGetters(PARTY_NAMESPACE, [
      'PeBackgroundOverviewCardLock',
      'peParty',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  created () {
    this.initPeBackgroundOverviewCard()
    this.getDropdownOptions({ path: 'parties/father_status' })
    this.getDropdownOptions({ path: 'parties/mother_status' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardUpdate () {
      return this.PeBackgroundOverviewCardUpdate({ id: this.peParty.id, section: this.peParty })
    },

    onDateFirstEntryUncertainUpdate (value) {
      this.$v.peParty.date_first_entry.$touch()
      this.$v.peParty.date_first_entry_uncertain.$touch()
      this.peParty.date_first_entry_uncertain = value
    },

    onDateFirstEntryUpdate (value) {
      this.$v.peParty.date_first_entry.$touch()
      this.$v.peParty.date_first_entry_uncertain.$touch()
      this.peParty.date_first_entry = value
    },

    onDateLastEntryUncertainUpdate (value) {
      this.$v.peParty.date_last_entry.$touch()
      this.$v.peParty.date_last_entry_uncertain.$touch()
      this.peParty.date_last_entry_uncertain = value
    },

    onDateLastEntryUpdate (value) {
      this.$v.peParty.date_last_entry.$touch()
      this.$v.peParty.date_last_entry_uncertain.$touch()
      this.peParty.date_last_entry = value
    },

    async onSectionUnlock () {
      return this.setPeBackgroundOverviewCardLock(false)
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'setPeBackgroundOverviewCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'PeBackgroundOverviewCardUpdate',
      'initPeBackgroundOverviewCard',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
